import { useEffect, useRef } from 'react';

/**
 * Hook for auto-clearing dependent selections when parent changes
 * Consolidates the cascade clear logic into one hook
 *
 * @param {Object} urlState - Current URL state
 * @param {Function} setUrlState - Function to update URL state
 * @param {Function} clearDependentEnrichedData - Function to clear enriched data
 */
export function useAutoClear(urlState, setUrlState, clearDependentEnrichedData) {
  const prevCulturesRef = useRef(urlState.cultures);
  const prevTreatmentTypeRef = useRef(urlState.treatmentType);
  const prevApplicationTechniqueRef = useRef(urlState.applicationTechnique);

  // Auto-clear when cultures change
  useEffect(() => {
    const prevCultures = prevCulturesRef.current || [];
    const currentCultures = urlState.cultures || [];

    const culturesChanged =
      JSON.stringify([...prevCultures].sort()) !==
      JSON.stringify([...currentCultures].sort());

    // Only clear if cultures changed AND there were previous cultures
    if (culturesChanged && prevCultures.length > 0) {
      setUrlState({
        treatmentType: null,
        applicationTechnique: null,
        products: [],
      });
      clearDependentEnrichedData('cultures');
    }

    prevCulturesRef.current = currentCultures;
  }, [urlState.cultures, setUrlState, clearDependentEnrichedData]);

  // Auto-clear when treatment type changes
  useEffect(() => {
    const prevType = prevTreatmentTypeRef.current;

    // Only clear if changed AND there was a previous value
    if (prevType && prevType !== urlState.treatmentType) {
      setUrlState({
        applicationTechnique: null,
        products: [],
      });
      clearDependentEnrichedData('treatmentType');
    }

    prevTreatmentTypeRef.current = urlState.treatmentType;
  }, [urlState.treatmentType, setUrlState, clearDependentEnrichedData]);

  // Auto-clear when application technique changes
  useEffect(() => {
    const prevTechnique = prevApplicationTechniqueRef.current;

    // Only clear if changed AND there was a previous value
    if (prevTechnique && prevTechnique !== urlState.applicationTechnique) {
      setUrlState({
        products: [],
      });
      clearDependentEnrichedData('applicationTechnique');
    }

    prevApplicationTechniqueRef.current = urlState.applicationTechnique;
  }, [urlState.applicationTechnique, setUrlState, clearDependentEnrichedData]);
}

export default useAutoClear;
